import React from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  addNestedDocumentsAndFolders,
  setExpandedFolderIds,
  setExpandedFoldersCache,
  setLastMovedEvent,
} from 'store/slices/dataDocumentsSlice';
import { fetchAvailableDocuments } from 'store/reducers/documentsCreator';
import { IDocument, IFolder } from 'interfaces';
import { useTranslation } from 'react-i18next';
import api from 'api';
import prepareDocuments from 'helpers/prepareDocuments';
import { encodeFiltersToBase64 } from 'helpers/encodeFiltersToBase64';

type TableRecord = (IDocument | IFolder) & {
  _nestingLevel?: number;
};

export function useFolderExpand() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const {
    documents,
    nestedDocuments,
    folders,
    nestedFolders,
    lastMovedEvent,
    expandedFolderIds: expandedFolderIdsArray,
    expandedFoldersCache,
    currentFolder,
    appliedFilters,
  } = useAppSelector((store) => store.documents);

  const { messageApi } = useAppSelector((store) => store.windowState);
  const { userFoldersPermissions, foldersForPermissions } = useAppSelector((store) => store.permissions);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);

  const [loadingFolderIds, setLoadingFolderIds] = React.useState<Set<string>>(new Set());

  const expandedFolderIds = React.useMemo(
    () => new Set(expandedFolderIdsArray),
    [expandedFolderIdsArray],
  );

  const loadAndCacheFolderContents = React.useCallback(
    async (folderId: string) => {
      setLoadingFolderIds((prev) => new Set(prev).add(folderId));
      try {
        const [subFoldersRes, docsRes] = await Promise.all([
          api.getFoldersOfFolder(folderId),
          api.getDocuments({
            room_id: dataRoom?.id!,
            folder_id: folderId,
            action: 'view',
            ...(encodeFiltersToBase64(appliedFilters) ? { filters: encodeFiltersToBase64(appliedFilters)! } : {}),
          }),
        ]);

        const targetFolderId = currentFolder[0]?.id || folderId;
        const currentPermissions =
          userFoldersPermissions?.find((folder) => folder.id === targetFolderId)?.permissions ||
          foldersForPermissions?.find((folder) => folder.id === targetFolderId)?.permissions;

        const subFoldersWithPermissions = subFoldersRes.data.map((folder) => ({
          ...folder,
          permissions: currentPermissions,
        }));
        const items = prepareDocuments(docsRes.data, subFoldersWithPermissions) as TableRecord[];
        
        dispatch(setExpandedFoldersCache({ ...expandedFoldersCache, [folderId]: items }));

        dispatch(
          addNestedDocumentsAndFolders({
            documents: docsRes.data,
            folders: subFoldersWithPermissions,
          }),
        );
      } catch {
        messageApi?.error(t('Documents.error.loadFolder'));
        throw new Error('load_failed');
      } finally {
        setLoadingFolderIds((prev) => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      }
    },
    [dataRoom?.id, currentFolder, userFoldersPermissions, foldersForPermissions, dispatch, t, messageApi, expandedFoldersCache, appliedFilters],
  );

  // Синхронизация кэша при изменении документов/папок или фильтров
  React.useEffect(() => {
    const allEntitiesMap = new Map<string, TableRecord>();
    for (const doc of documents) if (doc.id) allEntitiesMap.set(doc.id, doc);
    for (const doc of nestedDocuments) if (doc.id) allEntitiesMap.set(doc.id, doc);
    for (const folder of folders) if (folder.id) allEntitiesMap.set(folder.id, folder);
    for (const folder of nestedFolders) if (folder.id) allEntitiesMap.set(folder.id, folder);

    const next = { ...expandedFoldersCache };
    let hasChanges = false;

    for (const [folderId, items] of Object.entries(next)) {
      let folderChanged = false;
      const updatedItems = items
        .filter((item) => {
          const exists = item.id && allEntitiesMap.has(item.id);
          if (!exists) folderChanged = true;
          return exists;
        })
        .map((item) => {
          const latest = allEntitiesMap.get(item.id!);
          if (latest) {
            const nameChanged = latest.name !== item.name;
            const versionsChanged =
              latest.type === 'document' &&
              (latest as IDocument).version_count !== (item as IDocument).version_count;
            const permissionsChanged = latest.permissions !== item.permissions;
            const isConfidentialChanged = latest.is_confidential !== item.is_confidential;
            const documentsAmountChanged =
              latest.type === 'folder' &&
              (latest as IFolder).documents_amount !== (item as IFolder).documents_amount;
            const subfoldersChanged =
              latest.type === 'folder' &&
              (latest as IFolder).subfolders?.length !== (item as IFolder).subfolders?.length;

            if (nameChanged || versionsChanged || permissionsChanged || isConfidentialChanged || documentsAmountChanged || subfoldersChanged) {
              folderChanged = true;
              return { ...item, ...latest };
            }
          }
          return item;
        });

      if (folderChanged) {
        next[folderId] = updatedItems;
        hasChanges = true;
      }
    }

    if (hasChanges) dispatch(setExpandedFoldersCache(next));
  }, [documents, nestedDocuments, folders, nestedFolders, expandedFoldersCache, appliedFilters, dispatch]);

  // Обработка события перемещения сущности (обновляет нужную папку)
  React.useEffect(() => {
    if (!lastMovedEvent) return;

    const { targetFolderId } = lastMovedEvent;
    dispatch(setLastMovedEvent(null));

    if (targetFolderId === null) {
      // dispatch(fetchAvailableDocuments({ roomId: dataRoom?.id! }));
    } else if (expandedFolderIds.has(targetFolderId)) {
      loadAndCacheFolderContents(targetFolderId);
    }
  }, [lastMovedEvent, expandedFolderIds, dataRoom?.id, dispatch, loadAndCacheFolderContents]);

  // Перефетчиваем раскрытые папки при смене фильтров — кэш должен быть синхронен с корневой директорией
  const loadAndCacheFolderContentsRef = React.useRef(loadAndCacheFolderContents);
  React.useEffect(() => {
    loadAndCacheFolderContentsRef.current = loadAndCacheFolderContents;
  });

  const appliedFiltersRef = React.useRef(appliedFilters);
  React.useEffect(() => {
    if (appliedFiltersRef.current === appliedFilters) return;
    appliedFiltersRef.current = appliedFilters;

    for (const folderId of expandedFolderIdsArray) {
      loadAndCacheFolderContentsRef.current(folderId);
    }
  }, [appliedFilters]);

  const collapseFolder = (folderId: string) => {
    dispatch(setExpandedFolderIds(expandedFolderIdsArray.filter((id) => id !== folderId)));
  };

  const expandFolder = (folderId: string) => {
    if (!expandedFolderIds.has(folderId)) {
      dispatch(setExpandedFolderIds([...expandedFolderIdsArray, folderId]));
    }
  };

  const toggleFolderExpand = React.useCallback(
    async (folderId: string, e: React.MouseEvent) => {
      e.stopPropagation();

      if (expandedFolderIds.has(folderId)) {
        collapseFolder(folderId);
        return;
      }

      if (!expandedFoldersCache[folderId]) {
        try {
          await loadAndCacheFolderContents(folderId);
        } catch {
          return;
        }
      }

      expandFolder(folderId);
    },
    [expandedFolderIds, expandedFoldersCache, loadAndCacheFolderContents],
  );

  const findFolderById = React.useCallback(
    (id: string): TableRecord | undefined => {
      const rootFolder = folders.find((folder) => folder.id === id);
      if (rootFolder) return rootFolder;

      for (const cachedItems of Object.values(expandedFoldersCache)) {
        const nested = cachedItems.find(
          (item) => item.type === 'folder' && (item as IFolder).id === id,
        );
        if (nested) return nested;
      }

      return undefined;
    },
    [folders, expandedFoldersCache],
  );

  const buildFolderPath = React.useCallback(
    (folderRecord: TableRecord): TableRecord[] => {
      const path: TableRecord[] = [folderRecord];
      let parentId = (folderRecord as IFolder).parent_folder_id;

      while (parentId) {
        const parentFolder = findFolderById(parentId);
        if (!parentFolder) break;

        path.unshift(parentFolder);
        parentId = (parentFolder as IFolder).parent_folder_id;
      }

      return path;
    },
    [findFolderById],
  );

  return {
    loadingFolderIds,
    expandedFolderIds,
    toggleFolderExpand,
    buildFolderPath,
  };
}
